import {
  CloseOutlined,
  LoadingOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import {
  Button,
  Form,
  Input,
  Modal,
  Select,
  Spin,
  Upload,
  message,
} from 'antd';
import 'cropperjs/dist/cropper.css';
import { useContext, useEffect, useRef, useState } from 'react';
import { Cropper, ReactCropperElement } from 'react-cropper';
import { CategoryEnum, S3PathEnum } from '../../__generated__/graphql';
import { ROUTES } from '../../common/constants';
import { fileUpload } from '../../common/utils';
import useRouter from '../../hooks/useRouter';
import UserGuideModal from './components/UserGuideModal';
import { UPDATE_FLOOR_PLAN, UPLOAD_FLOOR_PLANS } from './graphql/mutations';
import {
  GET_IMAGE_SIGNED_URL,
  GET_PROPERTY_TYPES,
  VALIDATE_BASE_IMAGE,
} from './graphql/queries';

//Import css
import { AppContext } from '../../AppContext';
import useDetectBackButton from '../../components/useDetectBackButton';
import { RoutePromptContext } from '../../RoutePromptContext';
import { AppActionType, AppContextType } from '../../types/appContext.type';
import { FloorPlanData } from '../../types/dashboard.type';
import { FormValues, SubmissionData } from '../../types/uploadFloorPlans.type';
import './styles/uploadFloorPlan.less';

type UploadFileListProps = {
  name: string;
  status: 'success' | 'error';
  url: string;
  errorMessage?: string;
};

const UploadFloorPlan = () => {
  const [form] = Form.useForm();
  const { navigate } = useRouter();
  const [file, setFile] = useState<File | null>(null);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isGuideModal, setIsGuideModal] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [uploadList, setUploadList] = useState<UploadFileListProps[]>([]);
  const cropperRef = useRef<ReactCropperElement>(null);
  const [croppedDimensions, setCroppedDimensions] = useState<{
    height: number;
    width: number;
  }>();
  const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(true);
  const client = useApolloClient();
  const {
    state: { floorPlanSequence, setTourGuide },
    dispatch,
  } = useContext(AppContext) as AppContextType;
  const routePromptContext = useContext(RoutePromptContext);
  if (!routePromptContext) return null;
  const { isDirty, setIsDirty } = routePromptContext;
  const modal = useDetectBackButton(isDirty);

  //reading data from localstorage
  useEffect(() => {
    if (setTourGuide) {
      setIsGuideModal(setTourGuide);
    }
    if (floorPlanSequence) {
      const baseImageAsset = floorPlanSequence?.assets?.find(
        (asset) =>
          (asset as { category?: CategoryEnum }).category === CategoryEnum.Base,
      );

      if (baseImageAsset && baseImageAsset.width && baseImageAsset.height) {
        setUploadList([
          {
            name:
              baseImageAsset?.key
                ?.split('floor-plan-base-image/')[1]
                ?.split('?')[0] || '',
            status: 'success',
            url:
              'backend/' +
              baseImageAsset?.key?.split('backend/')[1]?.split('?')[0],
          },
        ]);

        setCroppedDimensions({
          height: baseImageAsset?.height,
          width: baseImageAsset?.width,
        });

        form.setFieldsValue({
          name: floorPlanSequence?.name,
          propertyType: floorPlanSequence?.propertyType,
          floorNumber: floorPlanSequence?.floorNumber,
        });

        setIsSubmitDisabled(false);
      }
    }
  }, [setTourGuide, floorPlanSequence]);

  //query fetching
  const { data: propertytypesData } = useQuery(GET_PROPERTY_TYPES);
  const propertyoptions = propertytypesData?.getAllPropertyTypesWeb?.data?.map(
    (property) => ({
      value: property?.propertyName,
      label: property?.propertyName,
    }),
  );

  //mutations perform
  const [uploadFloorPlan, { loading: submitLoading }] =
    useMutation(UPLOAD_FLOOR_PLANS);
  const [updateFloorPlan, { loading: updateLoading }] =
    useMutation(UPDATE_FLOOR_PLAN);

  // handle submit button validation
  const handleFormChange = () => {
    const hasErrors = form
      .getFieldsError()
      .some((field) => field.errors.length > 0);
    const allFieldsTouched = form.isFieldsTouched(true);
    const hasValidUpload =
      uploadList.some((item) => item.status === 'success') &&
      !!croppedDimensions;

    setIsSubmitDisabled(!allFieldsTouched || hasErrors || !hasValidUpload);
  };

  useEffect(() => {
    handleFormChange();
  }, [uploadList, croppedDimensions]);

  const handleBeforeUpload = (file: File): boolean => {
    const isValidType =
      file.type === 'image/png' ||
      file.type === 'image/jpeg' ||
      file.type === 'image/jpg';
    const isValidSize = file.size / 1024 / 1024 <= 25;

    if (!isValidType) {
      message.error('You can only upload JPG, PNG, JPEG files!');
      return false;
    }
    if (!isValidSize) {
      message.error('Image must be smaller than 25MB!');
      return false;
    }

    if (uploadList.length > 0) {
      setIsDirty(true);
      dispatch({
        type: AppActionType.setRouterPrompt,
        data: true,
      });
    }

    // Remove any error files from uploadList before adding a new one
    setUploadList((prevList) =>
      prevList.filter((item) => item.status !== 'error'),
    );

    // Read file and update state
    const reader = new FileReader();
    setFile(file);
    reader.onload = () => {
      setImageSrc(reader.result as string);
      setIsModalVisible(true);
    };
    reader.readAsDataURL(file);

    return false;
  };

  const handleCrop = async () => {
    setIsModalVisible(false);
    if (uploadList.some((item) => item.status === 'success')) {
      message.warning('A file has already been uploaded.');
      return;
    }
    const cropper = cropperRef.current?.cropper;
    if (cropper && file) {
      setLoading(true);
      try {
        const croppedCanvas = cropper.getCroppedCanvas();
        const croppedHeight = croppedCanvas.height;
        const croppedWidth = croppedCanvas.width;
        setCroppedDimensions({ height: croppedHeight, width: croppedWidth });

        const croppedBlob: Blob = await new Promise((resolve, reject) =>
          croppedCanvas.toBlob((blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to crop the image.'));
            }
          }, file.type),
        );
        const croppedFile = new File([croppedBlob], file.name, {
          type: file.type,
          lastModified: Date.now(),
        });
        const signedUrlResponse = await client.query({
          query: GET_IMAGE_SIGNED_URL,
          variables: {
            data: {
              fileName: croppedFile.name,
              s3Path: S3PathEnum.FloorPlanBaseImage,
            },
          },
        });
        const signedUrl =
          signedUrlResponse?.data?.getImageSignedUrlWeb?.signedUrl;
        const signedUrlKey = signedUrlResponse?.data?.getImageSignedUrlWeb?.key;

        if (!signedUrl || !signedUrlKey) {
          throw new Error('Signed URL fetch failed.');
        }
        await fileUpload(signedUrl, croppedFile);
        const validateResponse = await client.query({
          query: VALIDATE_BASE_IMAGE,
          variables: { data: { baseImage: signedUrlKey } },
        });

        const validationResult = validateResponse?.data?.validateBaseImageWeb;

        if (!validationResult?.success) {
          throw new Error(
            validationResult?.message || 'Image validation failed.',
          );
        }

        setUploadList((prevList) => [
          ...prevList,
          { name: croppedFile.name, status: 'success', url: signedUrlKey },
        ]);

        //APIs throw custom errors that don't always inherit from Error, making it difficult to type
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        setUploadList((prevList) => [
          ...prevList,
          {
            name: file.name,
            status: 'error',
            url: '',
            errorMessage: error?.message || 'An error occurred during upload.',
          },
        ]);
        message.error(error?.message || 'An error occurred during upload.');
      } finally {
        setLoading(false);
      }
    } else {
      message.error('Crop the image before uploading.');
    }
  };

  const handleSubmit = async (values: FormValues) => {
    if (isDirty === false) {
      navigate(ROUTES.CHOOSE_DIRECTION);
      return;
    }

    if (uploadList.some((item) => item?.status === 'error')) {
      message.error('Fix upload errors before submitting.');
      return;
    }

    const height = croppedDimensions?.height ?? 0;
    const width = croppedDimensions?.width ?? 0;

    if (height === 0 || width === 0) {
      message.error(
        'Invalid image dimensions. Please ensure the image is cropped properly.',
      );
      return;
    }

    // Retrieve stored data from localStorage;
    let storedBaseImage = '';
    if (floorPlanSequence) {
      try {
        storedBaseImage =
          floorPlanSequence?.assets?.[0]?.key
            ?.split('backend/')[1]
            ?.split('?')[0] || '';
      } catch (error) {
        return false;
      }
    }

    const newBaseImage = uploadList?.length > 0 ? uploadList[0]?.url : '';

    const submissionData: SubmissionData = {
      name: values?.name,
      propertyType: values?.propertyType,
      floorNumber: parseInt(values?.floorNumber, 10),
      height,
      width,
      baseImage: newBaseImage || storedBaseImage,
    };

    try {
      let responseData;
      if (floorPlanSequence?.id) {
        const updateResponse = await updateFloorPlan({
          variables: {
            data: { ...submissionData, floorPlanId: floorPlanSequence.id },
          },
        });

        responseData = updateResponse?.data?.updateFloorPlanWeb?.data;
      } else {
        const uploadResponse = await uploadFloorPlan({
          variables: { data: submissionData },
        });

        responseData = uploadResponse?.data?.uploadFloorPlanWeb?.data;
      }

      if (responseData) {
        dispatch({
          type: AppActionType.setRouterPrompt,
          data: false,
        });
        dispatch({
          type: AppActionType.setFloorPlanSequence,
          data: responseData as FloorPlanData,
        });
        navigate(ROUTES.CHOOSE_DIRECTION);
      }
    } catch (error) {
      message.error('Submission failed. Please try again.');
    }
  };

  const handleModalClose = () => {
    setIsGuideModal(false);
  };

  // useDetectBackButton();

  return (
    <div className="upload-floor-plan-container">
      <UserGuideModal
        visible={isGuideModal}
        onClose={handleModalClose}
      ></UserGuideModal>
      {modal}
      <div className="upload-floor-plan-form-wrapper">
        <Form
          form={form}
          layout="vertical"
          className="upload-floor-plan-form"
          onFieldsChange={handleFormChange}
          onFinish={handleSubmit}
        >
          <div className="upload-floor-plan-image-container">
            <Upload
              accept="image/x-png, image/jpeg, image/jpg"
              listType="picture"
              maxCount={1}
              showUploadList={false}
              beforeUpload={handleBeforeUpload}
            >
              <div className="upload-floor-plan-image-button-div">
                <div>
                  <Button
                    className="upload-btn"
                    type="primary"
                    icon={<PlusOutlined />}
                    disabled={uploadList.some(
                      (item) => item.status === 'success',
                    )}
                  />
                  <br />
                  {/* upload-image-container-text  */}
                  <span className="upload-image-container-text body-md semibold">
                    Upload floor plan
                  </span>
                  <br />
                  <span className="validation-image-text">
                    JPG, PNG, JPEG formats, up to 25 MB
                  </span>
                </div>
              </div>
            </Upload>
            <div className="uploaded-files-list">
              {uploadList.length > 0 && (
                <div className={`upload-item ${uploadList[0].status}`}>
                  <div className="header">
                    <div className="name-container">
                      {uploadList[0].status === 'success' ? (
                        <div>{uploadList[0].name}</div>
                      ) : (
                        <div style={{ display: 'flex' }}>
                          <div>{uploadList[0].name}</div>
                        </div>
                      )}
                    </div>
                    <Button
                      type="text"
                      icon={<CloseOutlined />}
                      onClick={() => {
                        setFile(null);
                        setUploadList([]);
                        setImageSrc(null);
                        setIsDirty(true);
                        dispatch({
                          type: AppActionType.setRouterPrompt,
                          data: true,
                        });
                      }}
                    />
                  </div>
                  {uploadList[0].status !== 'success' && (
                    <div className="status-message">{`Error: ${uploadList[0].errorMessage}`}</div>
                  )}
                </div>
              )}
            </div>
            {loading && (
              <Spin
                indicator={<LoadingOutlined spin />}
                className="validation-loader"
              />
            )}
          </div>
          <Form.Item
            label={
              <span className="body-meta medium">
                Plan/Site Name <span className="required-input-icon">*</span>
              </span>
            }
            name="name"
            rules={[
              { required: true, message: 'Please Enter your Plan/Site name!' },
              {
                max: 50,
                message: 'Plan/Site name cannot exceed 50 characters!',
              },
              {
                validator: (_, value) => {
                  if (value && value.trimStart() !== value) {
                    return Promise.reject(
                      new Error('Only spaces are not allowed!'),
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              placeholder="e.g. My Office"
              className="floor-plan-input"
              onChange={() => {
                setIsDirty(true);
                dispatch({
                  type: AppActionType.setRouterPrompt,
                  data: true,
                });
              }}
            />
          </Form.Item>
          <Form.Item
            label={
              <span className="body-meta medium">
                Property Type <span className="required-input-icon">*</span>
              </span>
            }
            name="propertyType"
            rules={[
              { required: true, message: 'Please Select property type!' },
            ]}
          >
            <Select
              onChange={() => {
                setIsDirty(true);
                dispatch({
                  type: AppActionType.setRouterPrompt,
                  data: true,
                });
              }}
              placeholder="Select Property Type"
              className="floor-plan-select"
              options={propertyoptions}
            />
          </Form.Item>
          <Form.Item
            label={
              <span className="body-meta medium">
                Floor Number <span className="required-input-icon">*</span>
              </span>
            }
            name="floorNumber"
            rules={[
              { required: true, message: 'Please enter your Floor Number!' },
              {
                validator: (_, value) =>
                  value > 4999
                    ? Promise.reject(new Error('Floor Number is not valid!'))
                    : Promise.resolve(),
              },
            ]}
          >
            <Input
              placeholder="e.g. 4"
              type="number"
              className="floor-plan-input"
              min={0}
              onChange={() => {
                setIsDirty(true);
                dispatch({
                  type: AppActionType.setRouterPrompt,
                  data: true,
                });
              }}
              max={4999}
              onKeyDown={(e) => {
                if (e.key === 'e' || e.key === '+' || e.key === '-') {
                  e.preventDefault();
                }
              }}
            />
          </Form.Item>
          <Button
            htmlType="submit"
            className="continue-button"
            type="primary"
            disabled={
              isSubmitDisabled ||
              submitLoading ||
              updateLoading ||
              uploadList.length === 0
            }
          >
            Continue
          </Button>
        </Form>
      </div>
      {/* Cropper Modal */}
      <Modal
        width={800}
        open={isModalVisible}
        title="Crop Image"
        onCancel={() => setIsModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsModalVisible(false)}>
            Cancel
          </Button>,
          <Button key="crop" type="primary" onClick={handleCrop}>
            Crop
          </Button>,
        ]}
      >
        {imageSrc && (
          <Cropper
            className="cropper"
            src={imageSrc}
            style={{ height: '400px', width: '100%' }}
            aspectRatio={NaN}
            guides={true}
            ref={cropperRef}
            viewMode={1}
            background={false}
            autoCropArea={1}
            responsive={true}
            checkOrientation={false}
          />
        )}
      </Modal>
    </div>
  );
};

export default UploadFloorPlan;
